import React, { useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE } from "../lib/api";
import { ArrowLeft, ShieldCheck, XCircle, HelpCircle, Search, Loader2 } from "lucide-react";

// Pagina pubblica: inserendo il codice riportato sull'attestato si verifica
// l'autenticità tramite l'endpoint di verifica pubblico (senza autenticazione).
// È la stessa verifica del QR code, ma per chi ha solo il codice scritto.
export default function VerificaAttestato() {
  const [code, setCode] = useState("");
  const [state, setState] = useState({ loading: false, data: null, error: null });

  const verify = async (e) => {
    e?.preventDefault();
    const c = code.trim();
    if (!c) return;
    setState({ loading: true, data: null, error: null });
    try {
      const res = await fetch(`${API_BASE}/public/verify/${encodeURIComponent(c)}`);
      const data = await res.json();
      setState({ loading: false, data, error: null });
    } catch (err) {
      setState({ loading: false, data: null, error: "Verifica non riuscita. Riprova tra qualche istante." });
    }
  };

  const { loading, data, error } = state;

  return (
    <div data-testid="verify-certificate-page" className="min-h-screen bg-brand-bg">
      <header className="border-b border-brand-line bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-blue transition-colors">
            <ArrowLeft size={16} /> Torna alla home
          </Link>
          <img
            src={`${process.env.PUBLIC_URL}/logo-academy-full.png`}
            alt="Buro Academy"
            className="h-7 w-auto"
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-center mb-8">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-sm bg-brand-blue/10 text-brand-blue mb-4">
            <ShieldCheck size={28} />
          </div>
          <h1 className="text-3xl sm:text-4xl text-brand-ink mb-2">Verifica un attestato</h1>
          <p className="text-brand-muted max-w-xl mx-auto">
            Inserisci il codice riportato sull'attestato per accertarne l'autenticità.
            Il codice si trova in fondo al documento, accanto al QR code.
          </p>
        </div>

        <form onSubmit={verify} className="max-w-xl mx-auto flex flex-col sm:flex-row gap-2" data-testid="verify-form">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            data-testid="verify-code-input"
            placeholder="Codice dell'attestato"
            aria-label="Codice dell'attestato"
            autoComplete="off"
            className="flex-1 rounded-sm border border-brand-line bg-white px-4 py-3 text-brand-ink placeholder:text-brand-muted focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
          />
          <button
            type="submit"
            disabled={loading || !code.trim()}
            data-testid="verify-submit"
            className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            Verifica
          </button>
        </form>

        {error && (
          <p className="text-center text-sm text-red-700 mt-4" data-testid="verify-error">{error}</p>
        )}

        {data && (
          <div className="mt-8">
            {data.status === "valid" ? (
              <div data-testid="verify-valid" className="border border-emerald-200 bg-emerald-50 rounded-sm p-8 text-center">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-sm bg-emerald-600 text-white mb-4">
                  <ShieldCheck size={32} />
                </div>
                <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-1">Certificato valido</div>
                <h2 className="text-2xl text-emerald-900 mb-4">Autenticità verificata</h2>
                <p className="text-emerald-900 leading-relaxed max-w-2xl mx-auto">
                  Si certifica che <strong>{data.first_name} {data.last_name}</strong> ha superato il corso{" "}
                  <strong>«{data.course_title}»</strong> in data{" "}
                  <strong>{new Date(data.issued_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</strong>.
                </p>
                <div className="mt-6 text-xs text-emerald-800/70 font-mono">Codice: {data.code}</div>
              </div>
            ) : data.status === "revoked" ? (
              <div data-testid="verify-revoked" className="border border-red-200 bg-red-50 rounded-sm p-8 text-center">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-sm bg-red-600 text-white mb-4">
                  <XCircle size={32} />
                </div>
                <div className="text-xs uppercase tracking-widest text-red-700 font-semibold mb-1">Certificato revocato</div>
                <h2 className="text-2xl text-red-900 mb-4">Non più valido</h2>
                <p className="text-red-900 max-w-2xl mx-auto">
                  Il certificato con codice <strong>{data.code}</strong> è stato revocato
                  {data.revoked_at ? (
                    <> in data <strong>{new Date(data.revoked_at).toLocaleDateString("it-IT")}</strong></>
                  ) : null}
                  .
                </p>
              </div>
            ) : (
              <div data-testid="verify-not-found" className="border border-brand-line bg-white rounded-sm p-8 text-center">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-sm bg-brand-muted text-white mb-4">
                  <HelpCircle size={32} />
                </div>
                <div className="text-xs uppercase tracking-widest text-brand-muted font-semibold mb-1">Nessun riscontro</div>
                <h2 className="text-2xl text-brand-ink mb-4">Codice non riconosciuto</h2>
                <p className="text-brand-muted max-w-2xl mx-auto">
                  Non risulta alcun attestato con questo codice. Controlla di averlo digitato
                  esattamente come riportato sul documento.
                </p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
